import React, { memo, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Collapse, IconButton, CircularProgress } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import Scrollbars from 'react-custom-scrollbars';
import { useTranslation } from 'react-i18next';

import { withHealedCurrentPath } from 'hocs/withHealedCurrentPath';
import Modules from 'modules';
import useActions from 'modules/map/useActions';
import { sessionSelectors } from 'modules/session';
import { useHelpVideoManager } from 'hooks/useHelpVideoManager';
import Divider from 'core/ui/Divider';
import useSelectors from 'modules/map/useSelectors';
import { helpVideoModule } from 'modules/helpVideo';
import LoaderSm from 'core/ui/LoaderSm';
import Typography from 'core/ui/Typography';
import { useStyles } from './styles';

import { HelpVideoItem } from '../HelpVideoItem';
import { AdminArea } from '../AdminArea';

const HelpCollapseBase = memo(({ healedCurrentPath }) => {
  const { request } = useActions();

  const isAdmin = useSelector(sessionSelectors.getIsAdmin);

  const classes = useStyles();

  const [helpVideos, setHelpVideos] = useState([]);

  const getMappedHelpVideos = useCallback(() => {
    request({
      action: Modules.helpVideo.actions.indexByPath,
      data: {
        path: healedCurrentPath,
      },
      options: {
        onSuccess: setHelpVideos,
        onError: console.log,
      },
    });
  }, [request, healedCurrentPath]);

  const removeHelpVideoMap = (helpVideoId) => {
    request({
      action: Modules.helpVideo.actions.destroyByPath,
      data: { helpVideoId, params: { path: healedCurrentPath } },
      options: {
        onSuccess: getMappedHelpVideos,
      },
    });
  };

  useEffect(() => {
    getMappedHelpVideos();
  }, [getMappedHelpVideos]);

  return (
    <CollapseContainer>
      <Box className={classes.container}>
        <Divider />

        <AbsoluteLoader />

        {helpVideos.length >= 1 ? (
          <Scrollbars
            autoHeight
            autoHeightMin={288}
            autoHeightMax={350}
            renderView={(props) => <div {...props} className={classes.contentContainerStyle} />}
          >
            {helpVideos.map((helpVideo) => (
              <Box key={helpVideo.id} className={classes.itemWrapper}>
                <HelpVideoItem helpVideo={helpVideo} />

                {isAdmin && (
                  <IconButton
                    className={classes.removeButton}
                    onClick={() => removeHelpVideoMap(helpVideo.id)}
                  >
                    <CancelIcon />
                  </IconButton>
                )}
              </Box>
            ))}
          </Scrollbars>
        ) : (
          <ListEmptyComponent />
        )}

        {isAdmin && (
          <>
            <Divider mb={2} />

            <AdminArea
              getMappedHelpVideos={getMappedHelpVideos}
              currentHelpVideos={helpVideos}
              currentPath={healedCurrentPath}
            />
          </>
        )}
      </Box>
    </CollapseContainer>
  );
});

const CollapseContainer = ({ children }) => {
  const { visible } = useHelpVideoManager();

  return (
    <Collapse in={visible} mountOnEnter>
      {children}
    </Collapse>
  );
};

const AbsoluteLoader = () => {
  const classes = useStyles();

  const loading = useSelectors(helpVideoModule, 'loading');

  return (
    <Box className={classes.absoluteLoader}>
      <LoaderSm loading={loading} />
    </Box>
  );
};

const ListEmptyComponent = () => {
  const { t } = useTranslation();
  const classes = useStyles();

  const loading = useSelectors(helpVideoModule, 'loading');

  return (
    <Box className={classes.listEmptyComponent}>
      {loading ? (
        <CircularProgress className={classes.listEmptyComponentLoader} />
      ) : (
        <Typography className={classes.listEmptyComponentText}>
          {t('helparea.emptylist')}
        </Typography>
      )}
    </Box>
  );
};

export const HelpCollapse = withHealedCurrentPath(HelpCollapseBase);
